import React, { useState, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { login as apiLogin, signup as apiSignup } from '../services/api';
import cacheService from '../services/cacheService';
import styled from 'styled-components';
import {
  LogoContainer,
  Logo,
  BrandName
} from './auth/AuthStyles';  // Import the shared styles
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Input, Button, Form, PasswordField } from './auth/AuthForm'; // Import PasswordField
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { useHistory, Link } from 'react-router-dom';
import axios from 'axios';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000; // Increase this value to be higher than all other components
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #14464F;
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: underline;
  
  &:hover {
    color: #245052;
  }
`;

const Title = styled.h2`
  color: #14464F;
  margin: 20px 0;
  text-align: center;
  font-size: 20px;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin: 0.5rem 0;
  text-align: center;
  padding: 0.5rem;
  background-color: #ffe6e6;
  border-radius: 4px;
  border: 1px solid #dc3545;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  margin-top: 4px;
`;

const CheckboxLabel = styled.label`
  color: #666;
  font-size: 14px;
  line-height: 1.4;
`;

const TermsLink = styled(Link)`
  color: #14464F;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const AuthModal = ({ onClose, onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const { language } = useLanguage();
  const t = translations[language].auth;
  const history = useHistory();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!isLogin && !termsAccepted) {
      setError(t.errors.acceptTerms || 'Please accept the Terms and Conditions');
      setIsLoading(false);
      return;
    }

    try {
      let response;
      if (isLogin) {
        response = await apiLogin(email, password);
      } else {
        response = await apiSignup({ firstName, lastName, email, password });
      }
      await login(response.data.user, response.data.token);
      await transferLocalFavorites();
      if (onAuthSuccess) {
        onAuthSuccess(response.data.user, response.data.token);
      }
      onClose();
    } catch (error) {
      console.error('Authentication error:', error);
      const errorMessage = error.response?.data?.error || 
                          error.message || 
                          t.errors.default;
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const transferLocalFavorites = async () => {
    const localFavorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    if (localFavorites.length > 0) {
      try {
        for (const poiId of localFavorites) {
          await cacheService.addFavorite(poiId);
        }
        localStorage.removeItem('favorites');
      } catch (error) {
        console.error('Error transferring local favorites:', error);
        // You might want to show an error message to the user here
      }
    }
  };

  const handleSocialLogin = async (provider) => {
    setError('');
    try {
      throw new Error('not configured');
    } catch (error) {
      console.error(`${provider} login error:`, error);
      let errorMessage;
      errorMessage = `${provider.charAt(0).toUpperCase() + provider.slice(1)} login is not configured yet. Please try another method.`;
      setError(errorMessage);
    }
  };

  // Clear error when switching between login and signup
  const handleToggleMode = () => {
    setError('');
    setIsLogin(!isLogin);
  };

  const handleLogin = async (credentials) => {
    try {
      const response = await axios.post('/auth/login', credentials);
      const { token, user } = response.data;
      login(user, token);
      
      // Let the parent component know about the success
      if (onAuthSuccess) {
        onAuthSuccess(user, token);
      }

      // Close the modal
      onClose();

      // Redirect based on role
      if (user.role === 'operator') {
        history.push('/operator/dashboard');
      } else if (user.role === 'admin') {
        history.push('/admin');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.error || 'Login failed');
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <LogoContainer>
          <Logo src="/images/QQ-Icon.svg" alt="Quisqueya Quest" />
          <BrandName>Quisqueya Quest</BrandName>
        </LogoContainer>
        
        <Title>{isLogin ? t.welcomeBack : t.createAccount}</Title>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Form onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <Input
                type="text"
                placeholder={t.firstName}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <Input
                type="text"
                placeholder={t.lastName}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </>
          )}
          <Input
            type="email"
            placeholder={t.email}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <PasswordField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t.password}
          />
          {!isLogin && (
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                required
              />
              <CheckboxLabel htmlFor="terms">
                {language === 'en' ? 'I accept the ' : 'Acepto los '}
                <TermsLink to="/legal/terms" target="_blank">
                  {language === 'en' ? 'Terms and Conditions' : 'Términos y Condiciones'}
                </TermsLink>
              </CheckboxLabel>
            </CheckboxContainer>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (language === 'en' ? 'Loading...' : 'Cargando...') : (isLogin ? t.signIn : t.signUp)}
          </Button>
        </Form>
        
        <ToggleButton onClick={() => {
          if (isLoading) return;
          setError('');
          setIsLogin(!isLogin);
          if (!isLogin) {
            setTermsAccepted(false);
          }
        }} style={{ opacity: isLoading ? 0.5 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}>
          {isLogin ? t.noAccount : t.haveAccount}
        </ToggleButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AuthModal;
